import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/ApiService';
import type { BiltEvent } from '../../types/event';
import PageLoader from '../ui/PageLoader';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import { useOnboarding } from '../../hooks/useOnboarding';
import { JudgeGuideCard } from '../onboarding/OnboardingCards';
import { shouldShowJudgeGuide } from '../../utils/onboarding';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';

const JudgeDashboard: React.FC = () => {
  const onboarding = useOnboarding();
  const [events, setEvents] = useState<BiltEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      setEvents(await apiService.getJudgeEvents());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load assigned events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadEvents();
  }, []);

  if (loading) {
    return <PageLoader message="Loading assigned events…" />;
  }

  const showGuide = shouldShowJudgeGuide(onboarding.state);

  return (
    <div className="max-w-3xl space-y-6">
        <PageHeader
          title="Assigned events"
          description="Open an event to filter by class, continue drafts, and finalize scores. Progress saves automatically."
        />

        {showGuide && (
          <JudgeGuideCard onDismiss={() => void onboarding.dismissJudgeGuide()} />
        )}

        {error ? (
          <EmptyState
            icon={<ClipboardDocumentListIcon className="w-12 h-12" />}
            title="Couldn&apos;t load events"
            description={error}
            action={
              <Button type="button" variant="primary" className="min-h-[44px]" onClick={() => void loadEvents()}>
                Try again
              </Button>
            }
          />
        ) : events.length === 0 ? (
          <EmptyState
            icon={<ClipboardDocumentListIcon className="w-12 h-12" />}
            title="No assigned events yet"
            description="Once an organizer adds you as a judge, your events will appear here."
          />
        ) : (
          <ul className="space-y-3">
            {events.map((e) => (
              <li key={e.id}>
                <Link
                  to={`/judge/events/${e.id}`}
                  className="card-interactive block p-4 cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="heading-card">{e.title}</p>
                      <p className="text-meta mt-1.5">
                        {e.city && e.state ? `${e.city}, ${e.state}` : 'Location TBD'}
                      </p>
                    </div>
                    <span className="text-sm text-orange-300 shrink-0 mt-1">Score →</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
};

export default JudgeDashboard;
